/**
 * Utilities for syncing SearchPageV2 URL path with API canonical/cityUrl
 * without stripping amenity segments (e.g. /climate-control-storage-units/).
 *
 * Map-based search: when the user drags the map we push the current pathname
 * unchanged and only update the query (location=lat,lng). We do not build or replace location in the canonical URL. 
 */

const SELF_STORAGE_PREFIX = "/self-storage/";

/**
 * Normalize path: ensure leading slash, strip trailing slash.
 * @param {string} path - Path with or without leading/trailing slash
 * @returns {string}
 */
export function normalizePath(path) {
	if (!path || typeof path !== "string") return "";
	const trimmed = path.trim().replace(/\/+$/, "");
	return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * Extract pathname from canonical (full URL or path-only).
 * @param {string} canonical - Full URL or path like /self-storage/state/city/
 * @returns {string|null} Normalized path or null if invalid
 */
export function getPathFromCanonical(canonical) {
	if (!canonical || typeof canonical !== "string") return null;
	try {
		// Full URL
		if (canonical.startsWith("http://") || canonical.startsWith("https://")) {
			const path = new URL(canonical).pathname;
			return path.startsWith(SELF_STORAGE_PREFIX) ? normalizePath(path) : null;
		}
		// Path-only
		const p = canonical.startsWith("/") ? canonical : `/${canonical}`;
		return p.startsWith(SELF_STORAGE_PREFIX) ? normalizePath(p) : null;
	} catch {
		return null;
	}
}

/**
 * Number of path segments (e.g. /a/b/c -> 3).
 * @param {string} path - Normalized path
 * @returns {number}
 */
export function getPathSegmentCount(path) {
	return path ? path.split("/").filter(Boolean).length : 0;
}

/**
 * State/city key from path (e.g. /self-storage/california/mountain-view -> "california/mountain-view").
 * Used to detect same location so we do not overwrite URL on browser back (stale API state).
 * Comparison is case-insensitive so casing fixes still sync.
 * @param {string} pathNorm - Normalized path
 * @returns {string|null} "state/city" lowercased, or null if path has fewer than 3 segments
 */
function getStateCityKey(pathNorm) {
	const segments = pathNorm ? pathNorm.split("/").filter(Boolean) : [];
	return segments.length >= 3 ? `${segments[1]}/${segments[2]}`.toLowerCase() : null;
}

/**
 * Decide the target path to sync to: prefer canonical (includes amenity when present),
 * fall back to cityUrl. Returns null if we should not replace (e.g. preserve amenity
 * when current path has it but API returned city-only).
 *
 * @param {Object} opts
 * @param {string} opts.pathname - Current location pathname
 * @param {string} opts.cityUrl - City path from API (e.g. /self-storage/california/mountain-view/)
 * @param {string} [opts.canonical] - Full canonical URL or path from API
 * @returns {string|null} Target path with trailing slash to replace with, or null to skip replace
 */
export function getSearchPageSyncTargetPath({ pathname, cityUrl, canonical }) {
	if (!cityUrl || !pathname.startsWith(SELF_STORAGE_PREFIX)) {
		return null;
	}

	const pathNorm = normalizePath(pathname);
	const cityUrlNorm = normalizePath(cityUrl.startsWith("/") ? cityUrl : `/${cityUrl}`);

	let targetPathNorm = cityUrlNorm;
	const canonicalPath = getPathFromCanonical(canonical);
	if (canonicalPath) {
		targetPathNorm = canonicalPath;
	}

	// Preserve amenity segment when current path has more segments than target
	// (user is on amenity URL but API returned city-only canonical).
	if (getPathSegmentCount(pathNorm) > getPathSegmentCount(targetPathNorm)) {
		return null;
	}

	if (pathNorm === targetPathNorm) {
		return null;
	}

	// Do not overwrite URL when pathname and API target are different places (e.g. user
	// clicked back: URL is previous CLP but API state is still from the last search).
	const pathKey = getStateCityKey(pathNorm);
	const targetKey = getStateCityKey(targetPathNorm);
	if (pathKey && targetKey && pathKey !== targetKey) {
		return null;
	}

	return targetPathNorm.endsWith("/") ? targetPathNorm : `${targetPathNorm}/`;
}
